import React from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'Components/common'
import { formatDateTimeToDateTimeBR } from 'utils/date_utils'
import {
  TimelineWrap, TimelineLine, TimelineItem, TimelineDot,
  TimelineDate, TimelineCard, TimelineTitle, TimelineDesc,
} from './AssessmentDetail.styles'

const CATEGORY_COLOR = {
  creation: 'var(--vo-celeste)',
  vendor: 'var(--vo-celeste)',
  executive: 'var(--vo-warning)',
  review: 'var(--vo-success)',
  security: 'var(--vo-danger)',
}

// Real, dotted action strings (SPEC-AUDIT-004 FR-7) -> the pre-provisioned i18n keys under
// assessment.timeline.events. Falls back to the backend's own (English-only) actionLabel via
// t()'s defaultValue when an action isn't in this map yet — never a raw untranslated key.
const ACTION_EVENT_KEY = {
  'audit:assessment.created': 'created',
  'audit:assessment.invitationSent': 'invitationSent',
  'audit:assessment.submitted': 'vendorSubmitted',
  'audit:assessment.controlsUnlocked': 'controlsUnlocked',
  'audit:assessment.approved': 'assessmentApproved',
  'audit:assessment.executiveApprovalRequested': 'execApprovalRequested',
  'audit:executiveApproval.approved': 'executiveApproved',
  'audit:executiveApproval.rejected': 'executiveRejected',
  'audit:evidenceScan.completed': 'evidenceScanPassed',
  'audit:evidenceScan.failed': 'evidenceScanFailed',
  // SPEC-AUDIT-006
  'audit:assessment.invitationResent': 'invitationResent',
  'audit:assessment.trustScoreCalculated': 'trustScoreCalculated',
  'audit:assessment.trustScoreRecalculated': 'trustScoreRecalculated',
  'audit:assessment.evidenceUploaded': 'evidenceUploaded',
  'audit:assessment.trustScoreAutoCalculated': 'trustScoreAutoCalculated',
  'audit:assessment.vendorLinkRetrieved': 'vendorLinkRetrieved',
}

// A refused/failed attempt is logged under the same action as its success (result: 'failure'),
// so it needs its own label rather than reading as the event having happened.
const FAILURE_EVENT_KEY = {
  'audit:assessment.evidenceUploaded': 'evidenceUploadFailed',
  'audit:assessment.submitted': 'vendorSubmitRefused',
  'audit:assessment.executiveApprovalRequested': 'execApprovalRequestFailed',
  'audit:executiveApproval.approved': 'executiveApproveRefused',
  'audit:executiveApproval.rejected': 'executiveRejectRefused',
}

const CheckSVG = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const CrossSVG = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const DotIcon = ({ action, failed }) => {
  if (failed) return <CrossSVG />
  if (action === 'audit:assessment.approved' || action === 'audit:executiveApproval.approved') return <CheckSVG />
  if (action === 'audit:executiveApproval.rejected' || action === 'audit:evidenceScan.failed') return <CrossSVG />
  return null
}

export const AssessmentTimeline = ({ entries, isLoading, isError }) => {
  const { t } = useTranslation(['gap-analysis'])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
        <Spinner $size={28} />
      </div>
    )
  }

  if (isError) {
    return (
      <p style={{ fontFamily: "'Lato',sans-serif", fontSize: '14px', color: 'var(--vo-danger)', margin: 0, textAlign: 'center', padding: '16px 0' }}>
        {t('gap-analysis:assessment.timeline.unableToLoad')}
      </p>
    )
  }

  const sortedEntries = [...(entries || [])].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  )

  if (sortedEntries.length === 0) {
    return (
      <p style={{ fontFamily: "'Lato',sans-serif", fontSize: '14px', color: 'var(--vo-slate)', margin: 0, textAlign: 'center', padding: '16px 0' }}>
        {t('gap-analysis:assessment.timeline.noEvents')}
      </p>
    )
  }

  return (
    <TimelineWrap>
      <TimelineLine />
      {sortedEntries.map((entry) => {
        const failed = entry.result === 'failure'
        const eventKey = (failed && FAILURE_EVENT_KEY[entry.action]) || ACTION_EVENT_KEY[entry.action]
        const label = eventKey
          ? t(`gap-analysis:assessment.timeline.events.${eventKey}`, { defaultValue: entry.actionLabel })
          : entry.actionLabel

        return (
          <TimelineItem key={entry._id}>
            <TimelineDot $color={failed ? 'var(--vo-danger)' : (CATEGORY_COLOR[entry.actionCategory] || 'var(--vo-slate)')}>
              <DotIcon action={entry.action} failed={failed} />
            </TimelineDot>
            <TimelineDate>{formatDateTimeToDateTimeBR(entry.timestamp)}</TimelineDate>
            <TimelineCard>
              <TimelineTitle>{label}</TimelineTitle>
              {failed && entry.resultDetail && (
                <TimelineDesc>{t('gap-analysis:assessment.timeline.reason')}: {entry.resultDetail}</TimelineDesc>
              )}
              {entry.action === 'audit:assessment.controlsUnlocked' && Array.isArray(entry.details?.controlIds) && (
                <TimelineDesc>
                  {entry.details.controlIds.length} {t('gap-analysis:assessment.timeline.controlsUnlocked')}
                </TimelineDesc>
              )}
              {entry.details?.notes && (
                <TimelineDesc>{t('gap-analysis:assessment.timeline.notes')}: {entry.details.notes}</TimelineDesc>
              )}
              {entry.details?.justification && (
                <TimelineDesc>{t('gap-analysis:assessment.timeline.justification')}: {entry.details.justification}</TimelineDesc>
              )}
            </TimelineCard>
          </TimelineItem>
        )
      })}
    </TimelineWrap>
  )
}
